import _ from 'lodash';

const VALIDITY_KEYS = {
    BAD_INPUT: 'badInput',
    CUSTOM_ERROR: 'customError',
    FILE_NOT_UPLOADED: 'fileNotUploaded',
    PATTERN_MISMATCH: 'patternMismatch',
    RANGE_OVERFLOW: 'rangeOverflow',
    RANGE_UNDERFLOW: 'rangeUnderflow',
    STEP_MISMATCH: 'stepMismatch',
    TOO_LONG: 'tooLong',
    TOO_SHORT: 'tooShort',
    TYPE_MISMATCH: 'typeMismatch',
    VALID: 'valid',
    VALUE_MISSING: 'valueMissing'
};
const INITIAL_VALIDATE_DATA = {
    validity: {
        [VALIDITY_KEYS.BAD_INPUT]: false,
        [VALIDITY_KEYS.CUSTOM_ERROR]: false,
        [VALIDITY_KEYS.FILE_NOT_UPLOADED]: false,
        [VALIDITY_KEYS.PATTERN_MISMATCH]: false,
        [VALIDITY_KEYS.RANGE_OVERFLOW]: false,
        [VALIDITY_KEYS.RANGE_UNDERFLOW]: false,
        [VALIDITY_KEYS.STEP_MISMATCH]: false,
        [VALIDITY_KEYS.TOO_LONG]: false,
        [VALIDITY_KEYS.TOO_SHORT]: false,
        [VALIDITY_KEYS.TYPE_MISMATCH]: false,
        [VALIDITY_KEYS.VALID]: true,
        [VALIDITY_KEYS.VALUE_MISSING]: false
    },
    validationMessage: ''
};
const INVALID_MESSAGES = {
    [VALIDITY_KEYS.VALUE_MISSING]: 'value missing',
    [VALIDITY_KEYS.PATTERN_MISMATCH]: 'pattern mismatch',
    [VALIDITY_KEYS.RANGE_OVERFLOW]: 'range overflow',
    [VALIDITY_KEYS.RANGE_UNDERFLOW]: 'range underflow',
    [VALIDITY_KEYS.STEP_MISMATCH]: 'step mismatch',
    [VALIDITY_KEYS.TYPE_MISMATCH]: 'type mismatch',
    [VALIDITY_KEYS.FILE_NOT_UPLOADED]: 'file not uploaded'
};
const VALIDATORS_TYPES = {
    'TEXT_INPUT_TEXT': 'TEXT_INPUT_TEXT',
    'TEXT_INPUT_PASSWORD': 'TEXT_INPUT_PASSWORD',
    'TEXT_INPUT_NUMBER': 'TEXT_INPUT_NUMBER',
    'TEXT_INPUT_EMAIL': 'TEXT_INPUT_EMAIL',
    'TEXT_INPUT_URL': 'TEXT_INPUT_URL',
    'TEXT_INPUT_TEL': 'TEXT_INPUT_TEL',
    'TEXT_AREA': 'TEXT_AREA',
    'SELECT_INPUT': 'SELECT_INPUT',
    'RADIO_GROUP': 'RADIO_GROUP',
    'CHECKBOX_GROUP': 'CHECKBOX_GROUP',
    'CHECKBOX': 'CHECKBOX',
    'DATE_PICKER': 'DATE_PICKER',
    'FILE_UPLOADER': 'FILE_UPLOADER'
};
const ELEMENT_TYPES = {
    TEXT_INPUT: 'TextInput',
    TEXT_AREA: 'TextAreaInput',
    SELECT_INPUT: 'SelectableList',
    RADIO_GROUP: 'RadioGroup',
    CHECKBOX_GROUP: 'CheckboxGroup',
    CHECKBOX: 'CheckboxInput',
    DATE_PICKER: 'DatePicker',
    FILE_UPLOADER: 'FileUploader'
};
const TEXT_INPUT_TYPES = {
    TEXT: 'text',
    PASSWORD: 'password',
    NUMBER: 'number',
    EMAIL: 'email',
    URL: 'url',
    TEL: 'tel'
};
const ELEMENT_TYPE_TO_VALIDATOR_TYPE = {
    [ELEMENT_TYPES.TEXT_INPUT]: {
        [TEXT_INPUT_TYPES.TEXT]: VALIDATORS_TYPES.TEXT_INPUT_TEXT,
        [TEXT_INPUT_TYPES.PASSWORD]: VALIDATORS_TYPES.TEXT_INPUT_PASSWORD,
        [TEXT_INPUT_TYPES.NUMBER]: VALIDATORS_TYPES.TEXT_INPUT_NUMBER,
        [TEXT_INPUT_TYPES.EMAIL]: VALIDATORS_TYPES.TEXT_INPUT_EMAIL,
        [TEXT_INPUT_TYPES.URL]: VALIDATORS_TYPES.TEXT_INPUT_URL,
        [TEXT_INPUT_TYPES.TEL]: VALIDATORS_TYPES.TEXT_INPUT_TEL
    },
    [ELEMENT_TYPES.TEXT_AREA]: VALIDATORS_TYPES.TEXT_AREA,
    [ELEMENT_TYPES.SELECT_INPUT]: VALIDATORS_TYPES.SELECT_INPUT,
    [ELEMENT_TYPES.RADIO_GROUP]: VALIDATORS_TYPES.RADIO_GROUP,
    [ELEMENT_TYPES.CHECKBOX_GROUP]: VALIDATORS_TYPES.CHECKBOX_GROUP,
    [ELEMENT_TYPES.CHECKBOX]: VALIDATORS_TYPES.CHECKBOX,
    [ELEMENT_TYPES.DATE_PICKER]: VALIDATORS_TYPES.DATE_PICKER,
    [ELEMENT_TYPES.FILE_UPLOADER]: VALIDATORS_TYPES.FILE_UPLOADER
};
const UPLOAD_STATUSES = {
    NOT_STARTED: 'NOT_STARTED',
    STARTED: 'STARTED',
    FAILED: 'FAILED',
    DONE: 'DONE'
};

const getValidationMessage = (validityKey) => INVALID_MESSAGES[validityKey];
const addErrorToValidateData = (validateData, validityKey, validationMessage) => ({
    validity: _.defaults({[validityKey]: true, [VALIDITY_KEYS.VALID]: false}, validateData.validity),
    validationMessage
});

const addCustomValidityToValidateData = (validateData, validationMessage) => addErrorToValidateData(validateData, VALIDITY_KEYS.CUSTOM_ERROR, validationMessage || '');

const addErrorToValidateDataAndKeepMessage = (validateData, validityKey, validationMessage) => {
    const newValidationMessage = validateData.validationMessage || validationMessage || getValidationMessage(validityKey);

    return addErrorToValidateData(validateData, validityKey, newValidationMessage);
};

const emailRegExp = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const validUrlRegExp = /(http|ftp|https):\/\/[\w-]+(\.[\w-]+)+([\w.,@?^=%&amp;:\/~+#-]*[\w@?^=%&amp;\/~+#-])?/;

const isValidEmail = (value) => emailRegExp.test(value);
const isValidUrl = (value) => validUrlRegExp.test(value);
const getUploadStatus = (compState) => _.get(compState, 'uploadStatus', UPLOAD_STATUSES.NOT_STARTED);
const isUploaded = (compState) => getUploadStatus(compState) === UPLOAD_STATUSES.DONE;
const getFileMissingMessage = (compState) => {
    if (getUploadStatus(compState) === UPLOAD_STATUSES.FAILED){
        return _.get(compState, 'serverMessage', INVALID_MESSAGES[VALIDITY_KEYS.FILE_NOT_UPLOADED]);
    }

    return INVALID_MESSAGES[VALIDITY_KEYS.FILE_NOT_UPLOADED];
};

const validateRequired = (value, compData, compProps) => (validateData) => {
    const isRequired = _.get(compProps, 'required', false);

    if (!isRequired || value){
        return validateData;
    }

    return addErrorToValidateDataAndKeepMessage(validateData, VALIDITY_KEYS.VALUE_MISSING);
};
const validateArrValueRequired = (value, compData, compProps) => (validateData) => {
    const isRequired = _.get(compProps, 'required', false);

    if (!isRequired || !_.isEmpty(value)){
        return validateData;
    }

    return addErrorToValidateDataAndKeepMessage(validateData, VALIDITY_KEYS.VALUE_MISSING);
};
const validatePattern = (value, compData) => (validateData) => {
    const pattern = _.get(compData, 'pattern');

    if (!pattern || _.isEmpty(value)){
        return validateData;
    }

    // according to w3 spec: https://www.w3.org/TR/html5/forms.html#compiled-pattern-regular-expression
    const regExp = new RegExp(`^(?:${pattern})$`);

    return regExp.test(value) ? validateData : addErrorToValidateDataAndKeepMessage(validateData, VALIDITY_KEYS.PATTERN_MISMATCH);
};
const validateRangeOverflow = (value, compData) => (validateData) => {
    const max = _.get(compData, 'max');

    if (!_.isFinite(max) || _.isEmpty(value)){
        return validateData;
    }

    return _.toFinite(value) <= max ?
        validateData :
        addErrorToValidateDataAndKeepMessage(validateData, VALIDITY_KEYS.RANGE_OVERFLOW);
};
const validateRangeUnderflow = (value, compData) => (validateData) => {
    const min = _.get(compData, 'min');

    if (!_.isFinite(min) || _.isEmpty(value)){
        return validateData;
    }

    return _.toFinite(value) >= min ?
        validateData :
        addErrorToValidateDataAndKeepMessage(validateData, VALIDITY_KEYS.RANGE_UNDERFLOW);
};
const DEFAULT_STEP = 1;
const validateStepMismatch = (value) => (validateData) => {
    if (_.isEmpty(value)){
        return validateData;
    }

    return _.toFinite(value) % DEFAULT_STEP === 0 ?
        validateData :
        addErrorToValidateDataAndKeepMessage(validateData, VALIDITY_KEYS.STEP_MISMATCH);
};
const validateEmail = (value) => (validateData) => {
    if(_.isEmpty(value)){
        return validateData;
    }

    return isValidEmail(value) ?
        validateData :
        addErrorToValidateDataAndKeepMessage(validateData, VALIDITY_KEYS.TYPE_MISMATCH);
};
const validateUrl = (value) => (validateData) => {
    if(_.isEmpty(value)){
        return validateData;
    }

    return isValidUrl(value) ?
        validateData :
        addErrorToValidateDataAndKeepMessage(validateData, VALIDITY_KEYS.TYPE_MISMATCH);
};

const validateUploadStatus = (value, compProps, compState) => (validateData) => {
    const isRequired = _.get(compProps, 'required', false);
    const isAlreadyInvalid = !validateData.validity[VALIDITY_KEYS.VALID];

    if (!isRequired || !compState || isAlreadyInvalid){
        return validateData;
    }

    return isUploaded(compState) ?
        validateData :
        addErrorToValidateDataAndKeepMessage(validateData, VALIDITY_KEYS.FILE_NOT_UPLOADED, getFileMissingMessage(compState));
};

const validatorByType = {
    [VALIDATORS_TYPES.TEXT_INPUT_TEXT]: (value, compData, compProps) => _.flow([validateRequired(value, compData, compProps), validatePattern(value, compData)]),
    [VALIDATORS_TYPES.TEXT_INPUT_PASSWORD]: (value, compData, compProps) => _.flow([validateRequired(value, compData, compProps), validatePattern(value, compData)]),
    [VALIDATORS_TYPES.TEXT_INPUT_NUMBER]: (value, compData, compProps) => _.flow([validateRequired(value, compData, compProps), validateRangeUnderflow(value, compData), validateRangeOverflow(value, compData), validateStepMismatch(value)]),
    [VALIDATORS_TYPES.TEXT_INPUT_EMAIL]: (value, compData, compProps) => _.flow([validateRequired(value, compData, compProps), validateEmail(value), validatePattern(value, compData)]),
    [VALIDATORS_TYPES.TEXT_INPUT_URL]: (value, compData, compProps) => _.flow([validateRequired(value, compData, compProps), validateUrl(value), validatePattern(value, compData)]),
    [VALIDATORS_TYPES.TEXT_INPUT_TEL]: (value, compData, compProps) => _.flow([validateRequired(value, compData, compProps), validatePattern(value, compData)]),
    [VALIDATORS_TYPES.TEXT_AREA]: (value, compData, compProps) => _.flow([validateRequired(value, compData, compProps)]),
    [VALIDATORS_TYPES.SELECT_INPUT]: (value, compData, compProps) => _.flow([validateRequired(value, compData, compProps)]),
    [VALIDATORS_TYPES.RADIO_GROUP]: (value, compData, compProps) => _.flow([validateRequired(value, compData, compProps)]),
    [VALIDATORS_TYPES.CHECKBOX]: (value, compData, compProps) => _.flow([validateRequired(value, compData, compProps)]),
    [VALIDATORS_TYPES.CHECKBOX_GROUP]: (value, compData, compProps) => _.flow([validateArrValueRequired(value, compData, compProps)]),
    [VALIDATORS_TYPES.DATE_PICKER]: (value, compData, compProps) => _.flow([validateRequired(value, compData, compProps)]),
    [VALIDATORS_TYPES.FILE_UPLOADER]: (value, compData, compProps, compState) => _.flow([validateArrValueRequired(value, compData, compProps), validateUploadStatus(value, compProps, compState)])
};

const getValidatorByType = (validatorType, value, compData, compProps, compState) => {
    const validatorByTypeFunc = validatorByType[validatorType];
    if(validatorByTypeFunc) {
        return validatorByTypeFunc(value, compData, compProps, compState);
    }
    return (validateData) => validateData;
};

function extendGetValidatorByType(elementType, validatorsType, getValidatorByType) {
    if(validatorByType[validatorsType] || ELEMENT_TYPE_TO_VALIDATOR_TYPE[elementType] || VALIDATORS_TYPES[validatorsType]){
        return false;
    }

    validatorByType[validatorsType] = getValidatorByType;
    VALIDATORS_TYPES[validatorsType] = validatorsType;
    ELEMENT_TYPE_TO_VALIDATOR_TYPE[elementType] = validatorsType;
    return true;
}

const getValidator = (value, compData, compProps, compState) => {
    let elementType = compData.type;

    if (elementType === ELEMENT_TYPES.TEXT_INPUT){
        elementType += `.${compData.textType}`;
    }

    const validatorType = _.get(ELEMENT_TYPE_TO_VALIDATOR_TYPE, elementType);

    return getValidatorByType(validatorType, value, compData, compProps, compState);
};

const validateValue = (value, compData, compProps, compState) => {
    const validator = getValidator(value, compData, compProps, compState);

    return validator(INITIAL_VALIDATE_DATA);
};

const getCustomValidity = (validateData) => {
    const hasCustomError = _.get(validateData, 'validity.customError', false);

    return hasCustomError ? validateData.validationMessage : '';
};

const validators = {
    validateRequired,
    validatePattern,
    validateRangeUnderflow,
    validateRangeOverflow,
    validateStepMismatch,
    validateEmail,
    validateUrl
};

export {
    getCustomValidity,
    validateValue,
    addCustomValidityToValidateData,
    extendGetValidatorByType,
    validators,
    addErrorToValidateDataAndKeepMessage,
    VALIDITY_KEYS
};



// WEBPACK FOOTER //
// ./js/modules/privates/utils/valueValidation.es6